import React, { useImperativeHandle, useRef, forwardRef } from 'react';
import Turnstile, { useTurnstile } from 'react-turnstile';
import { TURNSTILE_SITEKEY } from '@src/configs';

const TurnstileWidget = forwardRef((props, ref) => {
  const turnstile = useTurnstile();
  const widgetIdRef = useRef(null);
  const timeoutRef = useRef(null);
  const resolve = useRef();

  const handleReset = () => {
    turnstile.reset(widgetIdRef.current);
  };

  useImperativeHandle(ref, () => ({
    async onVerify() {
      // Execute turnstile to get a fresh token
      turnstile.execute(widgetIdRef.current);
      // Wait for the token to be set via callback
      const verifyToken = await new Promise((rs) => {
        resolve.current = rs;

        // Timeout after 30 seconds
        timeoutRef.current = setTimeout(() => {
          rs(null);
          handleReset();
        }, 30000);
      });

      return verifyToken;
    },

    reset() {
      handleReset();
      // Clear timeout so that it won't run the function and resolve the promise with null
      clearTimeout(timeoutRef.current);
    },
  }));

  return (
    <Turnstile
      sitekey={TURNSTILE_SITEKEY}
      onSuccess={(token) => {
        resolve.current?.(token);
      }}
      onError={handleReset}
      onExpire={handleReset}
      onTimeout={handleReset}
      onLoad={(widgetId) => {
        widgetIdRef.current = widgetId;
      }}
      execution="execute"
      fixedSize
      theme="light"
      size="normal"
      refreshExpired="auto" // Automatically refresh the token when it expires
      retry="auto" // Automatically retry when failed the challenges
      retryInterval={5000} // Retry interval in milliseconds
      // Add style to hide the widget since it will be visible in parent container with default width and height.
      style={{
        position: 'absolute',
        width: '0px',
        height: '0px',
      }}
    />
  );
});

export default TurnstileWidget;
